import { uniqueId } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addOverlay, removeOverlay } from '../../Actions/Overlay.action';
import { translate } from '../../Services/translation';
import Icon from '../Icon';
import clsx from 'clsx';

const MegaMenu = ({
  open,
  onMegaMenuClose,
  onMouseOverMegaMenu,
  onMouseLeaveMegaMenu,
  link,
  blocks,
}) => {
  const dispatch = useDispatch();
  const blockContainerRef = useRef<HTMLDivElement>(null);
  const [id] = useState(uniqueId());
  const [
    activeSubMegaMenuLink,
    setActiveSubMegaMenuLink,
  ] = useState<any>();
  const [subMegaMenuTimer, setSubMegaMenuTimer] = useState<any>();

  const onSubLinkHover = (link) => {
    clearTimeout(subMegaMenuTimer);
    setSubMegaMenuTimer(
      setTimeout(() => {
        setActiveSubMegaMenuLink(link);
        setSubMegaMenuTimer(null);
      }, 50)
    );
  };

  useEffect(() => {
    if (!blockContainerRef?.current) return;
    blockContainerRef.current.innerHTML = '';
    for (let block of blocks) {
      blockContainerRef.current.appendChild(block);
    }
  }, [blockContainerRef, blocks]);

  useEffect(() => {
    if (open) {
      dispatch(addOverlay('MegaMenu-' + id, onMegaMenuClose));
    } else {
      dispatch(removeOverlay('MegaMenu-' + id));
    }
  }, [open]);

  const linkGroupClasses =
    'py-2 px-3 leading-tight no-underline flex justify-between items-center text-md hover:bg-gray-200 [&_h6]:font-satoshi [&_h6]:font-satoshi-bold [&_h6]:text-gray-900';

  return (
    <div
      className={clsx(
        'absolute top-full left-0 right-0 bg-white transition-opacity',
        open
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none'
      )}
      onMouseLeave={onMouseLeaveMegaMenu}
      onMouseOver={onMouseOverMegaMenu}
    >
      <span
        className="fixed right-0 p-4 cursor-pointer"
        onClick={onMegaMenuClose}
      >
        <Icon name="close-mega-menu" className="text-sm text-gray-900" />
      </span>
      <div className="container flex">
        <div className="py-4 w-1/5 border-r border-solid border-gray-300">
          {link.links?.length > 0 && (
            <a href={link.url} className={linkGroupClasses} tabIndex={-1}>
              <h6 className="text-sm tracking-buttons uppercase leading-loose font-satoshi font-satoshi-bold">
                {translate('first.level.show.all.text').replace(
                  '{MENU}',
                  link.name
                ) ?? <>&nbsp;</>}
              </h6>
            </a>
          )}
          {link.links?.length > 0 &&
            link.links
              .filter((link) => link.extraInfo != 'mobile')
              .map((subLink, idx) => (
                <a
                  key={link.url + subLink.url + 'mega-sub-' + idx}
                  className={clsx(
                    activeSubMegaMenuLink == subLink ||
                      (subLink.isSelected && 'bg-gray-200'),
                    linkGroupClasses
                  )}
                  href={subLink.url}
                  tabIndex={-1}
                  onClick={(e) => {
                    if (subLink.links?.length > 0) {
                      onSubLinkHover(subLink);
                    }
                  }}
                  onMouseOver={() => onSubLinkHover(subLink)}
                >
                  <h6 className="font-satoshi font-satoshi-bold text-md">
                    {subLink.name ?? <>&nbsp;</>}
                  </h6>
                  {subLink.links?.length > 0 && (
                    <Icon
                      name="chevron-right-nav"
                      className="text-lg text-gray-900"
                    />
                  )}
                </a>
              ))}
        </div>

        {activeSubMegaMenuLink?.links?.length > 0 && (
          <div
            className={clsx('py-4 w-4/5', blocks.length > 0 && 'w-[65%]')}
          >
            <a
              href={activeSubMegaMenuLink.url}
              className={clsx(linkGroupClasses, 'w-fit')}
            >
              <h6
                className="text-sm tracking-buttons font-satoshi font-satoshi-bold uppercase leading-loose"
                data-456
              >
                {translate('second.level.show.all.text')
                  .replace('{MENU}', activeSubMegaMenuLink.name)
                  .replace('{SECOND-MENU}', link.name) ?? <>&nbsp;</>}
              </h6>
            </a>
            <div className="grid lg:grid-cols-3 xl:grid-cols-5 !gap-0">
              {activeSubMegaMenuLink.links
                .filter(
                  (subSubLink) =>
                    subSubLink.name != '' &&
                    subSubLink.extraInfo != 'mobile'
                )
                .map((subSubLink, idx) => (
                  <a
                    key={
                      activeSubMegaMenuLink.url +
                      subSubLink.url +
                      activeSubMegaMenuLink.name +
                      idx +
                      'mega-sub-2'
                    }
                    className={clsx(
                      subSubLink.isSelected && 'bg-gray-200',
                      linkGroupClasses
                    )}
                    href={subSubLink.url}
                  >
                    {subSubLink.name}
                  </a>
                ))}
            </div>
          </div>
        )}
        {blocks.length > 0 && (
          <div className="w-1/5 contents" ref={blockContainerRef}></div>
        )}
      </div>
    </div>
  );
};

export default MegaMenu;
